import { Component, OnInit,HostListener  } from '@angular/core';
import { ActivatedRoute, Router, NavigationStart, NavigationEnd } from '@angular/router';
import { JwksValidationHandler, OAuthService, OAuthStorage } from 'angular-oauth2-oidc';
import { environment } from 'src/environments/environment';
import { authConfig } from '../app/core/auth/auth.config';
import { AuthenticationService } from './core/services/auth/auth.service';
import { Globals } from './globals/globals';
import { NgxSpinnerService } from 'ngx-spinner';
import { AppUpdateService } from './core/services/pwa/app-update.service';
import { PwaInstallService } from './core/services/pwa/pwa-install.service';
import { isAgentAnswerPath, rememberPageDeepLink, takePageDeepLink } from './core/auth/task-deep-link';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit{
  constructor(
    private _router: Router,
		private oauthService: OAuthService, public globals: Globals
		,private route: ActivatedRoute, private authStorage: OAuthStorage
		,private authService:AuthenticationService
		,private appUpdate: AppUpdateService
		,private pwaInstall: PwaInstallService
		,private spinner: NgxSpinnerService) {
	// Keep automatic spinner enabled for non-dashboard requests
	// Dashboard uses individual card loading via silentServerFetch parameter
	this.globals.spinnerAnimation = true;

	// Show initial loading spinner during app bootstrap
	this.spinner.show();

	localStorage.removeItem("queuesBtn");
  }
  
  params:any;
  // The OIDC client must be configured exactly once. Re-configuring it on every
  // NavigationEnd re-fetched openid-configuration + jwks, stacked duplicate
  // silent-refresh timers, and re-fired the discovery/user-profile events whose
  // re-navigation re-created PagesComponent/DashboardComponent — multiplying
  // every dashboard API call (Transactions, bot/instance status, Utilization,
  // search, MyWorkspaces, UserUnreadCount, IsLoggedIn, …).
  private oauthInitialized = false;

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKeydown(event: KeyboardEvent): void {
	// Check if the escape key was pressed while focus is on a form element
	const activeElement = document.activeElement as HTMLElement;
	const isFormElement = activeElement && (
		activeElement.tagName === 'INPUT' ||
		activeElement.tagName === 'SELECT' ||
		activeElement.tagName === 'TEXTAREA' ||
		activeElement.classList.contains('p-dropdown') ||
		activeElement.classList.contains('p-multiselect') ||
		activeElement.classList.contains('p-autocomplete') ||
		activeElement.closest('.p-dropdown-panel') ||
		activeElement.closest('.p-multiselect-panel') ||
		activeElement.closest('.p-autocomplete-panel') ||
		activeElement.closest('.p-calendar-panel') ||
		activeElement.closest('.p-overlaypanel')
	);
	
	// Only close sidebar if not interacting with form elements
	if (!isFormElement) {
		this.globals.closeActiveSidebar();
	}
  }
  
  ngOnInit(): void {
	// Watch for a newer deployed build and offer a reload. No-op during `ng serve`
	// and on any build without the service worker.
	this.appUpdate.init();

	// Offer "Add to Home screen" on phones and tablets only.
	this.pwaInstall.init();

	// Fallback: Hide spinner after 10 seconds to prevent permanent black screen
	setTimeout(() => {
		this.spinner.hide();
	}, 10000);

	// P6-WP10: a signed-out person opening a page link (a Task Center item, a document from the
	// ontology review queue, a bookmark) keeps it for after sign-in.
	try {
		if (!this.authService.IsLoggedInOIDC()) {
			rememberPageDeepLink(window.location.pathname + window.location.search);
		}
	} catch { }

	let token = this.authStorage.getItem('access_token');

	let tenantUniqueKey = this.authStorage.getItem('TenantUniqueKey');

	this.route.queryParams.subscribe(params=>{
	
		this.params = params;
		if (params['TenantUniqueKey'] != null && params['TenantUniqueKey'] != undefined){
			this.globals.TenantUniqueKey=params['TenantUniqueKey'];
			this.authStorage.setItem('TenantUniqueKey',this.globals.TenantUniqueKey);
		}
		else{
		}
	});
	
	this._router.events.subscribe(event => {
	  if (event instanceof NavigationStart) {
	  } else if (event instanceof NavigationEnd) {
		// Hide the initial bootstrap spinner after navigation completes
		this.spinner.hide();

		// Get the current value of the query params
		const queryPrms = this.route.snapshot.queryParams;
		
		if ('formid' in queryPrms){
			this.globals.FormID = queryPrms['formid'];
		}

		// Configure OIDC only on the first navigation. Re-running this on every
		// NavigationEnd re-fetched openid-configuration/jwks, stacked duplicate
		// silent-refresh timers and triggered a navigation storm that re-created
		// PagesComponent/DashboardComponent — multiplying every dashboard API
		// call. Auth-error re-logins still go through the interceptor/topbar,
		// which are unaffected by this guard.
		// P6-WP10: the external answer page is public — no sign-in, no OIDC set-up (like /form,
		// but it never needs a session at all).
		if (isAgentAnswerPath(event.url)) {
			return;
		}
		if (this.oauthInitialized) {
			return;
		}
		this.oauthInitialized = true;

		if (event.url.split('?')[0].split('#')[0] == "/form")
		{
			if ('id' in queryPrms){
				this.globals.FormID = queryPrms['id'];
			}

			this.authService.configureOAuth();
		}
		else {
			const forceTenantSelection = queryPrms['force_tenant_selection'];
			if (forceTenantSelection) {
				// AccountAdmin navigating from Accounts portal - force fresh OAuth login
				// with force_tenant_selection parameter for identity server tenant selection
				this.authService.clearOIDCTokens();
				this.authService.configureOAuthAndLogin({ force_tenant_selection: 1 });
			}
			else if (!this.authService.IsLoggedInOIDC()) {
				this.authService.configureOAuthAndLogin();
			}
			else{
				this.authService.configureOAuth();
			}
		}
	  }
	});
	
	this.oauthService.events.subscribe(event => {
		if (event.type === 'token_received'){
			if (this.globals.userData == undefined ||
				this.globals.userData == null){
				this.oauthService.loadUserProfile().then(() => {
					// Using the loaded user data
					let claims = this.oauthService.getIdentityClaims();
					if (claims){
						this.globals.userData = claims;
					}
					
					// for reading state variables
					// let state = this.oauthService.state;
					  // if (state) {
						// state=decodeURIComponent(decodeURIComponent(state));
						
						 // const result: { [key: string]: string } = {};
						  // const pairs = state.split(';');
						  // pairs.forEach(pair => {
							// const [key, value] = pair.split(':');
							// result[decodeURIComponent(key)] = decodeURIComponent(value);
							// console.log(key,':', value);
						  // });
					  // } else {
						// console.log('No state parameter was passed.');
					  // }
				});
			}
		}
		else if (event.type === 'user_profile_loaded'){
			// Hide spinner after user profile is successfully loaded
			this.spinner.hide();

			if (!this.globals.FormID){
				// Get the current value of the query params
				const queryPrms = this.route.snapshot.queryParams;

				if ('formid' in queryPrms){
					this.globals.FormID = queryPrms['formid'];
				}
			}

			let urlPath = this._router.url.split('?')[0].split('#')[0];

			// P6-WP10: never move a person off the public answer page.
			if (isAgentAnswerPath(urlPath)) {
				return;
			}

			if (this.globals.TenantUniqueKey != null &&
				this.globals.TenantUniqueKey != undefined &&
				this.globals.TenantUniqueKey != ''){
				// P6-WP10: back from sign-in with a kept page link of this tenant: open it.
				const deepLink = takePageDeepLink(this.globals.TenantUniqueKey);
				if (deepLink && !urlPath.startsWith('/' + this.globals.TenantUniqueKey + '/')) {
					this._router.navigateByUrl(deepLink);
					return;
				}
				// Only navigate to default route if user isn't already on a tenant-scoped page
				if (!urlPath.startsWith('/' + this.globals.TenantUniqueKey + '/')) {
					this._router.navigate(['/' + this.globals.TenantUniqueKey + '/cloudautomation']);
				}
			}
			else if (urlPath != "/form")
			{
				if (this.globals.FormID){
					this._router.navigate(['form'], {
					  queryParams: { id: this.globals.FormID }
					});
				}
				else
				{
					this._router.navigate(['platformadmin'])
				}
			}
		}
		else if (event.type === 'token_refresh_error' || event.type === 'invalid_nonce_in_state'){
			this.authService.login();
		} else if (event.type === 'discovery_document_loaded'){
			if (token != null && token != undefined){
				this.globals.TenantUniqueKey = tenantUniqueKey;
				if (this.globals.userData == undefined ||
					this.globals.userData == null){
					this.oauthService.loadUserProfile().then(() => {
						let claims = this.oauthService.getIdentityClaims();
						if (claims){
							this.globals.userData = claims;
						}
					}).catch((err => {
					}));
				}
			}
		}
	});
  }
}